import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  TruckIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  UserIcon,
  PhoneIcon,
  CalendarIcon,
  DocumentPlusIcon
} from '@heroicons/react/24/outline';
import apiClient from '../api/config';
import { useToastContext } from '../components/common/Toast';
import { handleApiError } from '../utils/errorHandler';
import CreateJobCardModal from '../components/jobcard/CreateJobCardModal';

interface Customer {
  id: number;
  name: string;
  phone_number: string;
  email: string;
}

interface Vehicle {
  id: number;
  customer_id: number;
  garage_id: number;
  customer?: Customer;
  make: string;
  model: string;
  year: number;
  color?: string;
  plate_number: string;
  vin?: string;
  engine_number?: string;
  mileage?: number;
  fuel_type?: string;
  transmission_type?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_service_date?: string;
  next_service_date?: string;
}

const VehiclesPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { showSuccess, showError } = useToastContext();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showJobCardModal, setShowJobCardModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    plate_number: '',
    vin: '',
    engine_number: '',
    mileage: 0,
    fuel_type: 'petrol',
    transmission_type: 'automatic',
    notes: ''
  });

  useEffect(() => {
    fetchVehicles();
  }, [currentPage, searchTerm]);

  useEffect(() => {
    if (showAddModal || showEditModal) {
      fetchCustomers();
    }
  }, [showAddModal, showEditModal]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/vehicles', {
        params: {
          page: currentPage,
          page_size: 20,
          search: searchTerm
        }
      });
      
      // Handle response based on backend format
      if (response.data.data) {
        setVehicles(response.data.data);
      } else if (response.data.vehicles) {
        setVehicles(response.data.vehicles);
      } else if (Array.isArray(response.data)) {
        setVehicles(response.data);
      } else {
        setVehicles([]);
      }
      
      // Handle pagination
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.pages || response.data.pagination.total_pages || 1);
      } else if (response.data.total && response.data.limit) {
        setTotalPages(Math.ceil(response.data.total / response.data.limit));
      } else if (response.data.total && response.data.page_size) {
        setTotalPages(Math.ceil(response.data.total / response.data.page_size));
      }
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await apiClient.get('/customers', {
        params: { page_size: 100 }
      });
      
      if (response.data.data) {
        setCustomers(response.data.data);
      } else if (response.data.customers) {
        setCustomers(response.data.customers);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const handleAddVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const vehicleData = {
        ...formData,
        customer_id: parseInt(formData.customer_id),
        mileage: formData.mileage || 0,
        garage_id: user?.garage_id
      };
      
      await apiClient.post('/vehicles', vehicleData);
      showSuccess(t('vehicleAddedSuccessfully'));
      setShowAddModal(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const handleUpdateVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVehicle) return;
    
    try {
      const vehicleData = {
        ...formData,
        customer_id: parseInt(formData.customer_id),
        mileage: formData.mileage || 0
      };
      
      await apiClient.put(`/vehicles/${selectedVehicle.id}`, vehicleData);
      showSuccess(t('vehicleUpdatedSuccessfully'));
      setShowEditModal(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const handleDeleteVehicle = async (id: number) => {
    if (!window.confirm(t('confirmDeleteVehicle'))) return;
    
    try {
      await apiClient.delete(`/vehicles/${id}`);
      showSuccess(t('vehicleDeletedSuccessfully'));
      fetchVehicles();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      plate_number: '',
      vin: '',
      engine_number: '',
      mileage: 0,
      fuel_type: 'petrol',
      transmission_type: 'automatic',
      notes: ''
    });
    setSelectedVehicle(null);
  };

  const openEditModal = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setFormData({
      customer_id: vehicle.customer_id.toString(),
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      color: vehicle.color || '',
      plate_number: vehicle.plate_number,
      vin: vehicle.vin || '',
      engine_number: vehicle.engine_number || '',
      mileage: vehicle.mileage || 0,
      fuel_type: vehicle.fuel_type || 'petrol',
      transmission_type: vehicle.transmission_type || 'automatic',
      notes: vehicle.notes || ''
    });
    setShowEditModal(true);
  };

  return (
    <div className={`container mx-auto px-4 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <TruckIcon className="h-8 w-8 mr-2 text-teal-600" />
            {t('vehicleManagement')}
          </h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('addVehicle')}
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchVehicles')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Vehicles Table */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-8">
            <TruckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('noVehiclesFound')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('plateNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('makeModel')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('year')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('owner')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('mileage')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehicles.map((vehicle) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {vehicle.plate_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {vehicle.make} {vehicle.model}
                      </div>
                      <div className="text-sm text-gray-500">
                        {vehicle.color}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {vehicle.customer?.name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {vehicle.customer?.phone_number || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.mileage ? `${vehicle.mileage.toLocaleString()} km` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedVehicle(vehicle);
                          setShowJobCardModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title={t('createJobCard')}
                      >
                        <DocumentPlusIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(vehicle)}
                        className="text-teal-600 hover:text-teal-900 mr-3"
                        title={t('editVehicle')}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteVehicle(vehicle.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('deleteVehicle')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {t('previous')}
              </button>
              <span className="px-4 py-1">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {t('next')}
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Add/Edit Vehicle Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {showEditModal ? t('editVehicle') : t('addVehicle')}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={showEditModal ? handleUpdateVehicle : handleAddVehicle}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customer')}
                  </label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  >
                    <option value="">{t('selectCustomer')}</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} - {customer.phone_number}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('plateNumber')}
                  </label>
                  <input
                    type="text"
                    value={formData.plate_number}
                    onChange={(e) => setFormData({ ...formData, plate_number: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('make')}
                  </label>
                  <input
                    type="text"
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('model')}
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('year')}
                  </label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('color')}
                  </label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('vin')}
                  </label>
                  <input
                    type="text"
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('engineNumber')}
                  </label>
                  <input
                    type="text"
                    value={formData.engine_number}
                    onChange={(e) => setFormData({ ...formData, engine_number: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('mileage')} (km)
                  </label>
                  <input
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => setFormData({ ...formData, mileage: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fuelType')}
                  </label>
                  <select
                    value={formData.fuel_type}
                    onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="petrol">{t('petrol')}</option>
                    <option value="diesel">{t('diesel')}</option>
                    <option value="hybrid">{t('hybrid')}</option>
                    <option value="electric">{t('electric')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('transmissionType')}
                  </label>
                  <select
                    value={formData.transmission_type}
                    onChange={(e) => setFormData({ ...formData, transmission_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="automatic">{t('automatic')}</option>
                    <option value="manual">{t('manual')}</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('notes')}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  {showEditModal ? t('updateVehicle') : t('addVehicle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Job Card Modal */}
      <CreateJobCardModal
        isOpen={showJobCardModal}
        onClose={() => {
          setShowJobCardModal(false);
          setSelectedVehicle(null);
        }}
        vehicle={selectedVehicle || undefined}
        onSuccess={() => {
          showSuccess(t('jobCardCreatedSuccessfully'));
          setShowJobCardModal(false);
          setSelectedVehicle(null);
        }}
      />
    </div>
  );
};

export default VehiclesPage;